import React, { useEffect, useState } from "react";
import {
  Form,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  Card,
  Typography,
  Modal,
} from "antd";
import api from "../../../../services/api";
import dayjs, { Dayjs } from "dayjs";
import apiBeta from "../../../../services/api-beta";

interface FreightSearchFormProps {
  onFinish: (values: any) => void;
  initialValues?: any;
}

const { Title, Text } = Typography;

interface ItemSelect {
  idItem: any;
  name: string;
}

interface PortoSelect {
  port_code: string;
  port_id: string;
  port_name: string;
}

const FreightSearchForm: React.FC<FreightSearchFormProps> = ({
  onFinish,
  initialValues,
}) => {
  const [form] = Form.useForm();
  const [tiposMercadoria, setTiposMercadoria] = useState<ItemSelect[]>([]);
  const [portosEmbarque, setPortosEmbarque] = useState<PortoSelect[]>([]);
  const [portosDescarga, setPortosDescarga] = useState<PortoSelect[]>([]);
  const [loading, setLoading] = useState(false);

  // Função para desabilitar datas fora do intervalo [hoje, hoje+45]
  const disabledDate = (current?: Dayjs) => {
    if (!current) return false;
    const todayStart = dayjs().startOf("day");
    const maxEnd = todayStart.add(45, "day").endOf("day");
    return current.startOf("day").isBefore(todayStart) || current.isAfter(maxEnd);
  };

  // Validação extra do campo (cobre digitação manual)
  const validateDateInRange = (_: any, value?: Dayjs) => {
    if (!value) return Promise.resolve();
    const todayStart = dayjs().startOf("day");
    const maxEnd = todayStart.add(45, "day").endOf("day");
    if (value.startOf("day").isBefore(todayStart) || value.isAfter(maxEnd)) {
      return Promise.reject(
        new Error("Selecione uma data entre hoje e os próximos 45 dias.")
      );
    }
    return Promise.resolve();
  };

  useEffect(() => {
    Promise.all([
      api.get("filters/portos_embarque"),
      api.get("filters/portos_descarga"),
      api.get("filters/tipos_mercadoria"),
    ]).then(([embarqueRes, descargaRes, mercadoriaRes]) => {
      setPortosEmbarque(embarqueRes.data);
      setPortosDescarga(descargaRes.data);
      setTiposMercadoria(mercadoriaRes.data);

      if (initialValues) {
        const selectedFromPort = embarqueRes.data.find(
          (porto: PortoSelect) =>
            porto.port_code === initialValues.port_from_code
        );

        const selectedToPort = descargaRes.data.find(
          (porto: PortoSelect) => porto.port_code === initialValues.port_to_code
        );

        form.setFieldsValue({
          tipoMercadoria: initialValues.tipoMercadoria,
          portoEmbarque: selectedFromPort?.port_code,
          portoDescarga: selectedToPort?.port_code,
          dataEmbarque: initialValues.date
            ? dayjs(initialValues.date)
            : undefined,
        });
      }
    });
  }, [form, initialValues]);

  const handleSubmit = async (values: any) => {
    const userId = localStorage.getItem("user_id");

    if (!userId || userId === "undefined") {
      Modal.warning({
        title: "Sessão Antiga Detectada",
        content:
          "Foi verificado que você possui uma sessão antiga do sistema. Para utilizar esse novo recurso é necessário deslogar e logar novamente.",
        okText: "Entendi",
      });
      return;
    }

    try {
      setLoading(true);

      const selectedFromPort = portosEmbarque.find(
        (porto) => porto.port_code === values.portoEmbarque
      );

      const selectedToPort = portosDescarga.find(
        (porto) => porto.port_code === values.portoDescarga
      );

      const formattedDate = dayjs(values.dataEmbarque).format("YYYY-MM-DD");
      const formattedDateFirebase = dayjs(values.dataEmbarque).format("YYYY-MM");

      const token = localStorage.getItem("access_token");

      const requestData = {
        from_code: selectedFromPort?.port_code,
        to_code: selectedToPort?.port_code,
        date: formattedDate,
      };

      const response = await apiBeta.post(`/quotes-freight/new`, requestData, {
        headers: {
          Authorization: `${token}`,
          "ngrok-skip-browser-warning": "true",
          "Content-Type": "application/json",
        },
      });

      onFinish({
        ...response.data,
        ...{
          date: formattedDate,
          date_firebase: formattedDateFirebase,
          port_from_code: selectedFromPort?.port_code,
          port_to_code: selectedToPort?.port_code,
          port_from_name: selectedFromPort?.port_name,
          port_to_name: selectedToPort?.port_name,
          tipoMercadoria: values.tipoMercadoria,
        },
      });
    } catch (error) {
      console.error("Erro ao buscar fretes:", error);
    } finally {
      setLoading(false);
    }
  };

  const formItemLayout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  };

  return (
    <Card className="w-full rounded-none" bordered={false}>
      <div className="w-full">
        <div className="mb-6">
          <Title level={4} className="mb-2">
            Busca de Fretes Marítimos
          </Title>
          <Text className="text-gray-500">
            Por favor, preencha os detalhes abaixo para encontrar as melhores
            opções de frete para sua carga. Todos os campos marcados com * são
            obrigatórios.
          </Text>
        </div>

        <Form
          {...formItemLayout}
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          className="w-full"
        >
          <Row gutter={[24, 0]} className="w-full">
            <Col xs={24} sm={24} md={12} lg={6} xl={6}>
              <Form.Item
                name="tipoMercadoria"
                label="Tipo de mercadoria *"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <Select
                  placeholder="Selecione o tipo de mercadoria"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {tiposMercadoria.map((tipoMercadoria) => (
                    <Select.Option
                      key={tipoMercadoria.idItem}
                      value={tipoMercadoria.idItem}
                    >
                      {tipoMercadoria.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={12} lg={6} xl={6}>
              <Form.Item
                name="portoEmbarque"
                label="Porto de embarque *"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <Select
                  placeholder="Selecione o porto de embarque"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {portosEmbarque.map((embarque) => (
                    <Select.Option
                      key={embarque.port_code}
                      value={embarque.port_code}
                    >
                      {embarque.port_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={12} lg={6} xl={6}>
              <Form.Item
                name="portoDescarga"
                label="Porto de descarga *"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <Select
                  placeholder="Selecione o porto de descarga"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {portosDescarga.map((descarga) => (
                    <Select.Option
                      key={descarga.port_code}
                      value={descarga.port_code}
                    >
                      {descarga.port_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={12} lg={6} xl={6}>
              <Form.Item
                name="dataEmbarque"
                label="Data de embarque *"
                rules={[
                  { required: true, message: "Campo obrigatório" },
                  { validator: validateDateInRange },
                ]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  className="w-full"
                  placeholder="DD/MM/YYYY"
                  disabledDate={disabledDate}
                  inputReadOnly
                />
              </Form.Item>
            </Col>
          </Row>

          <Row className="mt-6">
            <Col span={24} className="flex justify-end">
              <Form.Item className="mb-0">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="bg-blue-500 hover:bg-blue-600"
                  size="large"
                  loading={loading}
                >
                  {loading ? "Buscando..." : "Buscar Fretes"}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <div className="mt-4">
          <Text type="secondary" className="text-sm">
            Após preencher todos os campos, clique em "Buscar Fretes" para ver
            as opções disponíveis.
          </Text>
        </div>
      </div>
    </Card>
  );
};

export default FreightSearchForm;
